import Header from "../header";
import Footer from "../footer";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import ClaimViewModal from "./ClaimViewModal";
import { fetchClaimDetails, deleteDraft, loadRelationships, fetchAllClaims } from "apiCalls/applyClaimService";
import config from "config";
import { useLanguage } from "context/LanguageContext";

function formatDate(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

function mapListItemToClaim(item) {
    const isDisease = item?.risk_fund_claim_type_id === 1;
    const statusName = item?.application?.status?.name || "";
    const isDraft = statusName.toLowerCase() === "draft";
    return {
        id: isDraft ? "-" : (item?.application?.application_number || item?.application_id || "-"),
        encryptedId: btoa(String(item?.id)),
        claimType: isDisease ? "Disease" : "Death",
        isDraft,
        status: statusName || "-",
        loaneeName: item?.loanee_name || "-",
        society: item?.society_name || "-",
        loanNo: item?.loan_number || "-",
        loan: {
            name: item?.loanee_name || "-",
            aadhaar: item?.loanee_aadhaar_number || "-",
            mobile: item?.loanee_phone_number || "-",
            loanNo: item?.loan_number || "-",
            amount: "-",
            membership: item?.loanee_society_membership_number || "-",
            society: item?.society_name || "-",
            societyAddress: "-",
            dob: formatDate(item?.loanee_date_of_birth),
            birthCertificateNumber: "-",
        },
        claim: isDisease ? {
            diseaseCategory: "",
            diseaseCategoryName: "-",
            hospitalId: "",
            hospitalCategory: "",
            hospitalCategoryName: "-",
            medicalCertificateIssuedBy: "",
            dateOfIllness: item?.illness_start_date || "",
            dateOfMedicalCertificate: item?.medical_certificate_date || "",
        } : {
            claimantName: item?.claimant_name || "",
            relationship: item?.relationship_to_loanee_id || "",
            relationshipName: "-",
            otherRelationship: item?.other_relation || "",
            dateOfDeath: item?.date_of_death || "",
            deathCertNumber: item?.death_certificate_number || "",
            previousDiseaseRelief: item?.is_previous_relief_taken ? "yes" : "no",
            releaseAmount: item?.previous_relief_amount || "",
        },
    };
}

function safeDecode(val) {
    if (!val) return "";
    try { return atob(String(val)); } catch { return String(val); }
}

function mapApiResponseToClaim(apiData, relationships = []) {
    const d = apiData?.data || {};
    const loan = apiData?.loan || {};
    const member = apiData?.primary_loan_member || {};
    const society = loan?.society_branch?.society || {};
    const isDisease = d?.risk_fund_claim_type_id === 1;

    // Society info moved to d.society in new API response
    const societyInfo = d?.society || {};
    const societyAddr = societyInfo?.address || {};
    const societyAddress = [
        societyAddr?.building_name,
        societyAddr?.street,
        societyAddr?.district       // now a plain string, not {name:"..."}
    ].filter(Boolean).join(", ");

    const statusName = d?.application?.status?.name || "";
    const isDraft = statusName.toLowerCase() === "draft";

    return {
        id: d?.application?.application_number || d?.application_id || "-",
        encryptedId: d?.id,
        claimType: isDisease ? "Disease" : "Death",
        isDraft,
        status: statusName || "Submitted",
        loaneeName: d?.loanee_name || "-",
        society: societyInfo?.name || society?.name || "-",
        loanNo: d?.loan_number || "-",
        loan: {
            name: d?.loanee_name || "-",
            aadhaar: d?.loanee_aadhaar_number || member?.aadhaar_number || "-",
            mobile: d?.loanee_phone_number || "-",
            loanNo: d?.loan_number || "-",
            amount: loan?.sanctioned_amount || "-",
            membership: d?.loanee_society_membership_number || member?.society_membership_number || "-",
            society: societyInfo?.name || society?.name || "-",
            societyAddress: societyAddress || "-",
            dob: formatDate(d?.loanee_date_of_birth || member?.date_of_birth),
            birthCertificateNumber: member?.birth_certificate_number || "-",
        },
        attachments: (d?.attachments || []).map(a => ({
            id: a.id,
            type: a.type,
            fileName: a.file_name,
            url: `${config.apiHost}${a.url}`,
            label: a.other_document_name || "",
        })),
        claim: isDisease ? {
            diseaseCategory: d?.disease_id || "",
            diseaseCategoryName: d?.disease_name || "-",
            hospitalId: safeDecode(d?.hospital_id),
            hospitalCategory: safeDecode(d?.hospital_category?.id),
            hospitalCategoryName: d?.hospital_category?.name || "-",
            medicalCertificateIssuedBy: d?.hospital_name || "",
            dateOfIllness: d?.illness_start_date || "",
            dateOfMedicalCertificate: d?.medical_certificate_date || "",
        } : {
            claimantName: d?.claimant_name || "",
            relationship: d?.relationship_to_loanee?.id || "",
            relationshipName: d?.relationship_to_loanee?.name || "-",
            otherRelationship: d?.other_relation || "",
            dateOfDeath: d?.date_of_death || "",
            deathCertNumber: d?.death_certificate_number || "",
            previousDiseaseRelief: d?.is_previous_relief_taken ? "yes" : "no",
            releaseAmount: d?.previous_relief_amount || "",
        },
    };
}

function UserDashboard() {
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [claims, setClaims] = useState([]);
    const [loadingClaims, setLoadingClaims] = useState(true);
    const [claimsError, setClaimsError] = useState("");

    const [viewClaim, setViewClaim] = useState(null);
    const [viewErrorId, setViewErrorId] = useState(null);
    const [draftToDelete, setDraftToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [resumingId, setResumingId] = useState(null);
    const [viewingId, setViewingId] = useState(null);
    const PAGE_SIZE = 10;
    const relationshipsRef = useRef([]);

    useEffect(() => {
        const userId = sessionStorage.getItem("userId");
        if (!userId) return;
        fetchAllClaims(btoa(String(userId)))
            .then(res => {
                const items = Array.isArray(res?.data) ? res.data : [];
                setClaims(items.map(mapListItemToClaim));
                setLoadingClaims(false);
            })
            .catch(() => {
                setClaimsError(t.failedToLoadClaims);
                setLoadingClaims(false);
            });
    }, []);


    const handleResumeDraft = async (draft) => {
        const route = draft.claimType === "Disease" ? "/diseaseClaim" : "/deathClaim";
        setResumingId(draft.encryptedId);
        try {
            const res = await fetchClaimDetails(draft.encryptedId);
            const fresh = mapApiResponseToClaim(res, relationshipsRef.current);
            navigate(route, {
                state: { loan: fresh.loan, claim: fresh.claim, draftId: draft.encryptedId }
            });
        } catch {
            navigate(route, {
                state: { loan: draft.loan, claim: draft.claim, draftId: draft.encryptedId }
            });
        } finally {
            setResumingId(null);
        }
    };

    const handleViewClaim = async (claim) => {
        setViewingId(claim.encryptedId);
        setViewErrorId(null);
        try {
            const [detailsRes, relsRes] = await Promise.all([
                fetchClaimDetails(claim.encryptedId),
                loadRelationships().catch(() => ({ data: [] })),
            ]);
            const relationships = Array.isArray(relsRes?.data) ? relsRes.data : [];
            relationshipsRef.current = relationships;
            setViewClaim(mapApiResponseToClaim(detailsRes, relationships));
        } catch {
            setViewErrorId(claim.encryptedId);
        } finally {
            setViewingId(null);
        }
    };

    const handleDeleteDraft = async () => {
        if (!draftToDelete) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await deleteDraft(draftToDelete.encryptedId);
            setClaims(prev => prev.filter(c => c.encryptedId !== draftToDelete.encryptedId));
            setDraftToDelete(null);
        } catch (error) {
            setDeleteError(error.message || t.failedToDeleteDraft);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Header />

            {/* ==== Scroll up and progress start ==== */}
            <div id="scrollUp">
                <svg className="arrowup" viewBox="0 0 24 24" width="18" height="18">
                    <path d="M13 7.828V20h-2V7.828l-5.364 5.364-1.414-1.414L12 4l7.778 7.778-1.414 1.414L13 7.828z" fill="#fff" />
                </svg>
                <svg className="scrollprogress" width="40" height="40">
                    <circle className="progress-circle" cx="20" cy="20" r="18" strokeWidth="2" fill="none" stroke="#fff" strokeDasharray="113.1" strokeDashoffset="113.1" />
                </svg>
            </div>
            {/* ==== Scroll up and progress Ends ==== */}

            {/* ==== Page Banner Section Start ==== */}
            <section className="rs-page-banner rs-faq-page-banner pt-30 pb-30 mt-140">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-7">
                            <div className="rs-page-banner__content">
                                <h1 className="title">{t.myClaimsDashboard}</h1>
                                <p>{t.dashboardSubtitle}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* ==== Page Banner Section Ends ==== */}

            {/* ==== Page Section Start ==== */}
            <section className="rs-faq-page pt-50 pb-120">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-12">

                            {/* Start New Claim Card */}
                            <div className="card mb-5" style={{ backgroundColor: "#eaf4f0", border: "1px solid #c2ddd5", borderRadius: "10px" }}>
                                <div className="card-body p-4">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <h5 style={{ color: "#1a5c4a", fontWeight: "700" }}>{t.startNewClaim}</h5>
                                            <p className="text-muted mb-0">{t.startNewClaimDesc}</p>
                                        </div>
                                        <div className="col-md-4 text-end">
                                            <button type="button" className="btn btn-primary" onClick={() => navigate("/claimLoaneeIdentification")}>
                                                <i className="fa fa-plus-circle me-2"></i> {t.applyNewClaim}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* My Claims Table */}
                            <h4 style={{ color: "#1a5c4a", fontWeight: "700" }} className="mb-3">{t.myClaims}</h4>
                            <div className="table-responsive">
                                <table className="table" style={{ border: "1px solid #c2ddd5", borderRadius: "10px", overflow: "hidden" }}>
                                    <thead style={{ backgroundColor: "#eaf4f0" }}>
                                        <tr>
                                            <th style={{ color: "#1a5c4a" }}>{t.claimId}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.loaneeName}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.societyName}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.loanNumber}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.claimType}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.status}</th>
                                            <th style={{ color: "#1a5c4a" }}>{t.actions}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingClaims ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <span className="spinner-border spinner-border-sm me-2 text-secondary"></span>
                                                    {t.loadingClaims}
                                                </td>
                                            </tr>
                                        ) : claimsError ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-danger py-4">{claimsError}</td>
                                            </tr>
                                        ) : claims.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">{t.noClaimsYet}</td>
                                            </tr>
                                        ) : (() => {
                                            const allRows = [
                                                ...claims.filter(c => c.isDraft).map(c => ({ type: "draft", data: c })),
                                                ...claims.filter(c => !c.isDraft).map(c => ({ type: "claim", data: c })),
                                            ];
                                            const totalPages = Math.ceil(allRows.length / PAGE_SIZE);
                                            const pageRows = allRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
                                            return (
                                                <>
                                                    {pageRows.map((row, i) => row.type === "draft" ? (
                                                        <tr key={`draft-${i}`}>
                                                            <td>—</td>
                                                            <td>{row.data.loaneeName}</td>
                                                            <td>{row.data.society}</td>
                                                            <td>{row.data.loanNo}</td>
                                                            <td>{row.data.claimType === "Disease" ? t.diseaseClaimType : t.deathClaimType}</td>
                                                            <td><span className="badge p-2" style={{ backgroundColor: "#fff3cd", color: "#856404" }}>{t.draft}</span></td>
                                                            <td className="d-flex gap-2">
                                                                <button
                                                                    className="btn btn-sm btn-primary"
                                                                    disabled={resumingId === row.data.encryptedId}
                                                                    style={resumingId && resumingId !== row.data.encryptedId ? { pointerEvents: "none" } : {}}
                                                                    onClick={() => handleResumeDraft(row.data)}
                                                                >
                                                                    {resumingId === row.data.encryptedId ? <><span className="spinner-border spinner-border-sm me-1"></span>{t.loading}</> : t.resume}
                                                                </button>
                                                                <button className="btn btn-sm btn-danger" onClick={() => setDraftToDelete(row.data)}>{t.delete}</button>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        <tr key={`claim-${i}`}>
                                                            <td>{row.data.id}</td>
                                                            <td>{row.data.loaneeName}</td>
                                                            <td>{row.data.society}</td>
                                                            <td>{row.data.loanNo}</td>
                                                            <td>{row.data.claimType === "Disease" ? t.diseaseClaimType : t.deathClaimType}</td>
                                                            <td><span className="badge badge-verify-light p-2">{row.data.status.toLowerCase() === "submitted" ? t.submitted : row.data.status}</span></td>
                                                            <td>
                                                                <button className="btn btn-sm btn-secondary" disabled={!!viewingId} onClick={() => handleViewClaim(row.data)}>
                                                                    {viewingId === row.data.encryptedId ? <><span className="spinner-border spinner-border-sm me-1"></span>{t.loading}</> : t.view}
                                                                </button>
                                                                {viewErrorId === row.data.encryptedId && (
                                                                    <div className="text-danger small mt-1">{t.failedToLoad}</div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {totalPages > 1 && (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-3">
                                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        disabled={currentPage === 1}
                                                                        onClick={() => setCurrentPage(p => p - 1)}
                                                                    >
                                                                        <i className="fa fa-angle-left"></i> {t.prev}
                                                                    </button>
                                                                    {Array.from({ length: totalPages }, (_, idx) => (
                                                                        <button
                                                                            key={idx + 1}
                                                                            className={`btn btn-sm ${currentPage === idx + 1 ? "btn-primary" : "btn-outline-secondary"}`}
                                                                            onClick={() => setCurrentPage(idx + 1)}
                                                                        >
                                                                            {idx + 1}
                                                                        </button>
                                                                    ))}
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        disabled={currentPage === totalPages}
                                                                        onClick={() => setCurrentPage(p => p + 1)}
                                                                    >
                                                                        {t.next} <i className="fa fa-angle-right"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>

                            {/* Important Information */}
                            <div className="mt-4 p-4 d-flex align-items-start gap-3" style={{ backgroundColor: "#fffde7", border: "1px solid #e8e0c0", borderRadius: "12px" }}>
                                <div style={{ backgroundColor: "#fde9c8", borderRadius: "50%", width: "42px", height: "42px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <i className="fa fa-info" style={{ color: "#e07b1a", fontSize: "18px" }}></i>
                                </div>
                                <div>
                                    <h6 style={{ color: "#1a5c4a", fontWeight: "700", marginBottom: "6px" }}>{t.importantInformation}</h6>
                                    <p className="mb-0" style={{ color: "#4a4a4a", fontSize: "14px" }}>{t.claimVerificationNote}</p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
            {/* ==== Page Section Ends ==== */}

            <ClaimViewModal claim={viewClaim} onClose={() => setViewClaim(null)} />

            {draftToDelete && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: "#eaf4f0" }}>
                                <h5 className="modal-title" style={{ color: "#1a5c4a", fontWeight: "700" }}>{t.deleteDraft}</h5>
                                <button type="button" className="btn-close" onClick={() => { setDraftToDelete(null); setDeleteError(""); }} disabled={deleting}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-1">{t.deleteDraftConfirm}</p>
                                <p className="text-muted small mb-0">
                                    <strong>{draftToDelete.claimType === "Disease" ? t.diseaseClaimType : t.deathClaimType}</strong> — {draftToDelete.loaneeName} ({draftToDelete.loanNo})
                                </p>
                                {deleteError && <div className="alert alert-danger mt-3 mb-0 py-2">{deleteError}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => { setDraftToDelete(null); setDeleteError(""); }} disabled={deleting}>{t.cancel}</button>
                                <button className="btn btn-danger" onClick={handleDeleteDraft} disabled={deleting}>
                                    {deleting ? <><span className="spinner-border spinner-border-sm me-2"></span>{t.deleting}</> : t.delete}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
}

export default UserDashboard;
